/**
 * Tenant Identity Bindings Hooks (rbac-03b, #1286)
 *
 * React Query hooks for the 9303 "Tenant Bindings" tab on IdentityProviderDetail.
 * They list every tenant bound to a given IdP and let a platform admin bind a
 * new tenant (including to the built-in Aaim Platform IdP) or remove a binding.
 *
 * Transport: the shared `identityProviderClient` (typed-clients.ts), which the
 * platform-admin nginx proxies straight to ${AAIM_CORE_AUTH_URL}. The side-
 * effect import of `../api/client` guarantees `identityProviderClient.use(
 * authMiddleware())` has run so the operator Bearer is attached -- aaim-core
 * requires it on these routes (reads: fi_admin; writes: platform-admin).
 *
 * Verbs match the canonical contract verbatim (POST create, PUT update, DELETE
 * soft-deactivate) per schema-registry/identity-provider-registry.openapi.yaml.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { components } from '@aaimplatform/institutional-types/identity-provider-registry';
import { identityProviderClient } from '../api/typed-clients';
// Side-effect import: registers authMiddleware() on identityProviderClient so
// the operator Bearer is attached. Loaded here because the IdP-detail route may
// be reached without any sibling hook having pulled in ../api/client first.
import '../api/client';

type TenantIdentityBinding = components['schemas']['TenantIdentityBinding'];
type CreateTenantIdentityBindingRequest = components['schemas']['CreateTenantIdentityBindingRequest'];
type UpdateTenantIdentityBindingRequest = components['schemas']['UpdateTenantIdentityBindingRequest'];

const BINDINGS_KEY = 'tenantIdentityBindings';

// Inline structural narrowing (no named ad-hoc type): the openapi-fetch error
// payload is `unknown` at the boundary; extract a human message defensively.
function describeError(err: unknown, fallback: string): string {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  if (typeof err === 'object') {
    const env = err as { message?: unknown; error?: unknown };
    if (typeof env.message === 'string') return env.message;
    if (typeof env.error === 'string') return env.error;
    if (env.error && typeof env.error === 'object') {
      const inner = env.error as { message?: unknown };
      if (typeof inner.message === 'string') return inner.message;
    }
  }
  return fallback;
}

function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error || !result.data) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    throw new Error(describeError(result.error, `HTTP ${status}: ${statusText}`));
  }
  return result.data;
}

/**
 * List the tenant bindings for a given identity provider (cross-tenant; the
 * 9303 platform-global view). aaim-core returns every tenant bound to the
 * provider when the caller is a platform admin.
 */
export function useTenantIdentityBindings(providerId: string | undefined) {
  return useQuery({
    queryKey: [BINDINGS_KEY, { providerId }],
    queryFn: async (): Promise<TenantIdentityBinding[]> => {
      const result = await identityProviderClient.GET('/api/v1/tenant-identity-bindings', {
        params: { query: { identityProviderId: providerId! } },
      });
      const wrapped = unwrap(result);
      if (!('success' in wrapped) || !wrapped.success || !('data' in wrapped) || !wrapped.data) {
        throw new Error('Tenant bindings response missing data');
      }
      return wrapped.data as TenantIdentityBinding[];
    },
    enabled: !!providerId,
  });
}

/**
 * Bind a tenant to an identity provider. Spec: POST
 * /tenant-identity-bindings (createTenantIdentityBinding). This is the
 * verify-marker hook for rbac-03b (#1286): `useBindTenantIdp`.
 */
export function useBindTenantIdp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: CreateTenantIdentityBindingRequest,
    ): Promise<TenantIdentityBinding> => {
      const result = await identityProviderClient.POST('/api/v1/tenant-identity-bindings', {
        body,
      });
      const wrapped = unwrap(result);
      if (!('success' in wrapped) || !wrapped.success || !('data' in wrapped) || !wrapped.data) {
        throw new Error('Bind response missing data');
      }
      return wrapped.data as TenantIdentityBinding;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [BINDINGS_KEY, { providerId: variables.identityProviderId }],
      });
    },
  });
}

/**
 * Update a tenant-IdP binding. Spec: PUT /tenant-identity-bindings/{bindingId}.
 */
export function useUpdateTenantIdentityBinding(providerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      bindingId,
      body,
    }: {
      bindingId: string;
      body: UpdateTenantIdentityBindingRequest;
    }): Promise<TenantIdentityBinding> => {
      const result = await identityProviderClient.PUT(
        '/api/v1/tenant-identity-bindings/{bindingId}',
        { params: { path: { bindingId } }, body },
      );
      const wrapped = unwrap(result);
      if (!('success' in wrapped) || !wrapped.success || !('data' in wrapped) || !wrapped.data) {
        throw new Error('Update binding response missing data');
      }
      return wrapped.data as TenantIdentityBinding;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BINDINGS_KEY, { providerId }] });
    },
  });
}

/**
 * Remove a tenant-IdP binding (soft-deactivate). Spec: DELETE
 * /tenant-identity-bindings/{bindingId}. aaim-core suspends the binding and
 * returns the deactivated record; a primary binding cannot be removed (409).
 */
export function useRemoveTenantIdentityBinding(providerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (bindingId: string): Promise<string> => {
      const result = await identityProviderClient.DELETE(
        '/api/v1/tenant-identity-bindings/{bindingId}',
        { params: { path: { bindingId } } },
      );
      if (result.error) {
        const status = result.response?.status ?? 0;
        const statusText = result.response?.statusText ?? 'Unknown';
        throw new Error(describeError(result.error, `HTTP ${status}: ${statusText}`));
      }
      return bindingId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BINDINGS_KEY, { providerId }] });
    },
  });
}
